import { useState, useEffect, type FormEvent } from 'react';
import { api } from '../../lib/api';
import type { Route, Stop } from '../../lib/types';

export function RouteManager() {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [stops, setStops] = useState<Record<string, Stop[]>>({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showStopForm, setShowStopForm] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', type: 'both' });
  const [stopForm, setStopForm] = useState({ name: '', latitude: '', longitude: '', stop_order: '', estimated_time: '' });
  const [error, setError] = useState<string | null>(null);

  const loadRoutes = async () => {
    const { routes: r } = await api.getRoutes();
    setRoutes(r);
    // Load stops for each route
    const stopsMap: Record<string, Stop[]> = {};
    for (const route of r) {
      const { stops: s } = await api.getRoute(route.id);
      stopsMap[route.id] = s;
    }
    setStops(stopsMap);
    setLoading(false);
  };

  useEffect(() => { loadRoutes(); }, []);

  const handleCreateRoute = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name) { setError('Route name required'); return; }
    try {
      await api.createRoute({ name: form.name, type: form.type });
      setForm({ name: '', type: 'both' });
      setShowForm(false);
      loadRoutes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    }
  };

  const handleAddStop = async (e: FormEvent, routeId: string) => {
    e.preventDefault();
    try {
      await api.createStop(routeId, {
        name: stopForm.name,
        latitude: parseFloat(stopForm.latitude),
        longitude: parseFloat(stopForm.longitude),
        stop_order: parseInt(stopForm.stop_order),
        estimated_time: stopForm.estimated_time || null,
      });
      setStopForm({ name: '', latitude: '', longitude: '', stop_order: '', estimated_time: '' });
      setShowStopForm(null);
      loadRoutes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    }
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div>
      <div className="admin-section-header">
        <h2>Route Management</h2>
        <button className="btn-sm btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Route'}
        </button>
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleCreateRoute} aria-label="Add route form">
          <div className="admin-form-row">
            <div className="form-group">
              <label>Route Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Route B - Afternoon" required aria-label="Route name" />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} aria-label="Route type">
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
                <option value="both">Both</option>
              </select>
            </div>
          </div>
          {error && <div className="form-error">{error}</div>}
          <button type="submit" className="btn-sm btn-primary">Create Route</button>
        </form>
      )}

      {routes.map((route) => (
        <div key={route.id} className="admin-card route-card">
          <div className="admin-card-header">
            <span className="admin-card-title">{route.name}</span>
            <div>
              <span className="admin-badge badge-blue">{route.type}</span>
              <button className="btn-sm btn-outline" onClick={() => setShowStopForm(showStopForm === route.id ? null : route.id)} style={{ marginLeft: 8 }} aria-label={`Add stop to ${route.name}`}>
                + Stop
              </button>
            </div>
          </div>

          {showStopForm === route.id && (
            <form className="admin-form" onSubmit={(e) => handleAddStop(e, route.id)} style={{ margin: '12px 0' }} aria-label={`Add stop to ${route.name}`}>
              <div className="admin-form-row">
                <div className="form-group">
                  <label>Stop Name</label>
                  <input value={stopForm.name} onChange={(e) => setStopForm({ ...stopForm, name: e.target.value })} placeholder="e.g., Main Road" required aria-label="Stop name" />
                </div>
                <div className="form-group">
                  <label>Latitude</label>
                  <input type="number" step="any" value={stopForm.latitude} onChange={(e) => setStopForm({ ...stopForm, latitude: e.target.value })} required aria-label="Latitude" />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input type="number" step="any" value={stopForm.longitude} onChange={(e) => setStopForm({ ...stopForm, longitude: e.target.value })} required aria-label="Longitude" />
                </div>
                <div className="form-group">
                  <label>Order</label>
                  <input type="number" value={stopForm.stop_order} onChange={(e) => setStopForm({ ...stopForm, stop_order: e.target.value })} required aria-label="Stop order" />
                </div>
                <div className="form-group">
                  <label>ETA</label>
                  <input type="time" value={stopForm.estimated_time} onChange={(e) => setStopForm({ ...stopForm, estimated_time: e.target.value })} aria-label="Estimated time of arrival" />
                </div>
              </div>
              <button type="submit" className="btn-sm btn-primary">Add Stop</button>
            </form>
          )}

          {stops[route.id]?.length > 0 ? (
            <div className="stops-list">
              {stops[route.id].map((stop, i) => (
                <div key={stop.id} className="stop-item">
                  <span className="stop-number">{i + 1}</span>
                  <span className="stop-name">{stop.name}</span>
                  <span className="stop-time">{stop.estimated_time || '--'}</span>
                  <span className="stop-coords">{stop.latitude.toFixed(4)}, {stop.longitude.toFixed(4)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="admin-empty" style={{ padding: '12px' }}>No stops added yet.</div>
          )}
        </div>
      ))}
      {routes.length === 0 && <div className="admin-empty">No routes yet.</div>}
    </div>
  );
}
